import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { workflowStagesService, tasksService } from '../../services';
import './WorkflowAutomationConfig.css';

const WorkflowAutomationConfig = () => {
  const { user } = useAuth();
  const [workflowStages, setWorkflowStages] = useState([]);
  const [taskTemplates, setTaskTemplates] = useState([]);
  const [stageTaskConfigs, setStageTaskConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedStage, setSelectedStage] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [stagesData, templatesData] = await Promise.all([
        workflowStagesService.getAll(),
        tasksService.getTaskTemplates()
      ]);
      
      setWorkflowStages(stagesData);
      setTaskTemplates(templatesData);
      
      // Build stage-task configuration mapping
      const configs = stagesData.map(stage => ({
        stage,
        assignedTemplates: templatesData.filter(template => 
          template.assigned_stages?.some(assignedStage => assignedStage.id === stage.id)
        )
      }));
      
      setStageTaskConfigs(configs);
    } catch (error) {
      console.error('Failed to fetch workflow automation data:', error);
      setError('Failed to load workflow automation configuration. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignTemplate = async (stageId, templateId) => {
    try {
      await tasksService.assignTemplateToStage(templateId, stageId);
      setSuccess('Template assigned to workflow stage successfully');
      await fetchData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Failed to assign template:', error);
      setError('Failed to assign template. Please try again.');
    }
  };

  const handleUnassignTemplate = async (stageId, templateId) => {
    try {
      await tasksService.unassignTemplateFromStage(templateId, stageId);
      setSuccess('Template unassigned from workflow stage successfully');
      await fetchData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Failed to unassign template:', error);
      setError('Failed to unassign template. Please try again.');
    }
  };

  const handleBulkAssign = async (stageId, templateIds) => {
    try {
      await Promise.all(
        templateIds.map(templateId => tasksService.assignTemplateToStage(templateId, stageId))
      );
      setSuccess(`${templateIds.length} templates assigned successfully`);
      await fetchData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Failed to bulk assign templates:', error);
      setError('Failed to assign templates. Please try again.');
    }
  };

  const getAvailableTemplates = (stageId) => {
    const assignedTemplateIds = stageTaskConfigs
      .find(config => config.stage.id === stageId)
      ?.assignedTemplates.map(t => t.id) || [];
    
    return taskTemplates.filter(template => 
      template.is_active && !assignedTemplateIds.includes(template.id)
    );
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'communication': return '📞';
      case 'documentation': return '📄';
      case 'follow_up': return '⏰';
      case 'research': return '🔍';
      case 'booking': return '✈️';
      case 'payment': return '💳';
      default: return '📋';
    }
  };

  const openStageConfig = (stage) => {
    setSelectedStage(stage);
    setShowConfigModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading workflow automation configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Workflow Automation Configuration</h2>
          <p className="mt-1 text-sm text-gray-600">
            Configure which tasks are automatically created when clients move between workflow stages
          </p>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {/* Workflow Stages Configuration */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {stageTaskConfigs.map((config) => (
          <div key={config.stage.id} className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: config.stage.color }}
                  ></div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {config.stage.name}
                  </h3>
                </div>
                <button
                  onClick={() => openStageConfig(config.stage)}
                  className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                >
                  Configure
                </button>
              </div>

              {config.stage.description && (
                <p className="text-sm text-gray-600 mb-4">
                  {config.stage.description}
                </p>
              )}

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">
                    Automated Tasks ({config.assignedTemplates.length})
                  </span>
                </div>

                {config.assignedTemplates.length === 0 ? (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    No automated tasks configured
                  </div>
                ) : (
                  <div className="space-y-2">
                    {config.assignedTemplates.slice(0, 3).map((template) => (
                      <div
                        key={template.id}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded"
                      >
                        <div className="flex items-center space-x-2">
                          <span className="text-lg">{getCategoryIcon(template.category)}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {template.title}
                            </p>
                            <div className="flex items-center space-x-2 mt-1">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(template.priority)}`}>
                                {template.priority}
                              </span>
                              {template.estimated_hours && (
                                <span className="text-xs text-gray-500">
                                  {template.estimated_hours}h
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleUnassignTemplate(config.stage.id, template.id)}
                          className="text-red-600 hover:text-red-900 text-xs"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    {config.assignedTemplates.length > 3 && (
                      <div className="text-center text-sm text-gray-500">
                        +{config.assignedTemplates.length - 3} more tasks
                      </div>
                    )}
                  </div>
                )}

                <div className="pt-2 border-t border-gray-200">
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        handleAssignTemplate(config.stage.id, e.target.value);
                        e.target.value = '';
                      }
                    }}
                    className="w-full text-xs border border-gray-300 rounded px-2 py-1"
                    defaultValue=""
                  >
                    <option value="">Add task template...</option>
                    {getAvailableTemplates(config.stage.id).map((template) => (
                      <option key={template.id} value={template.id}>
                        {getCategoryIcon(template.category)} {template.title}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Stage Configuration Modal */}
      {showConfigModal && selectedStage && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 xl:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div
                    className="w-6 h-6 rounded-full"
                    style={{ backgroundColor: selectedStage.color }}
                  ></div>
                  <h3 className="text-xl font-medium text-gray-900">
                    Configure {selectedStage.name}
                  </h3>
                </div>
                <button
                  onClick={() => setShowConfigModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-6">
                {/* Current Assigned Templates */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-3">
                    Assigned Task Templates
                  </h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {stageTaskConfigs
                      .find(config => config.stage.id === selectedStage.id)
                      ?.assignedTemplates.map((template) => (
                        <div
                          key={template.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        >
                          <div className="flex items-center space-x-3">
                            <span className="text-xl">{getCategoryIcon(template.category)}</span>
                            <div>
                              <h5 className="font-medium text-gray-900">{template.title}</h5>
                              <div className="flex items-center space-x-2 mt-1">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(template.priority)}`}>
                                  {template.priority}
                                </span>
                                <span className="text-xs text-gray-500 capitalize">
                                  {template.category}
                                </span>
                                {template.estimated_hours && (
                                  <span className="text-xs text-gray-500">
                                    {template.estimated_hours}h
                                  </span>
                                )}
                                {template.due_days_offset && (
                                  <span className="text-xs text-gray-500">
                                    Due: +{template.due_days_offset}d
                                  </span>
                                )}
                              </div>
                              {template.description && (
                                <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                  {template.description}
                                </p>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => handleUnassignTemplate(selectedStage.id, template.id)}
                            className="text-red-600 hover:text-red-900 font-medium"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Available Templates */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-3">
                    Available Task Templates
                  </h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {getAvailableTemplates(selectedStage.id).length === 0 ? (
                      <div className="text-center py-4 text-gray-500">
                        No available templates to assign
                      </div>
                    ) : (
                      getAvailableTemplates(selectedStage.id).map((template) => (
                        <div
                          key={template.id}
                          className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                        >
                          <div className="flex items-center space-x-3">
                            <span className="text-xl">{getCategoryIcon(template.category)}</span>
                            <div>
                              <h5 className="font-medium text-gray-900">{template.title}</h5>
                              <div className="flex items-center space-x-2 mt-1">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(template.priority)}`}>
                                  {template.priority}
                                </span>
                                <span className="text-xs text-gray-500 capitalize">
                                  {template.category}
                                </span>
                                {template.estimated_hours && (
                                  <span className="text-xs text-gray-500">
                                    {template.estimated_hours}h
                                  </span>
                                )}
                              </div>
                              {template.description && (
                                <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                  {template.description}
                                </p>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => handleAssignTemplate(selectedStage.id, template.id)}
                            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                          >
                            Assign
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-6 mt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowConfigModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Statistics */}
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Automation Summary
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {workflowStages.length}
              </div>
              <div className="text-sm text-gray-500">Workflow Stages</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {taskTemplates.filter(t => t.is_active).length}
              </div>
              <div className="text-sm text-gray-500">Active Templates</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {stageTaskConfigs.reduce((total, config) => total + config.assignedTemplates.length, 0)}
              </div>
              <div className="text-sm text-gray-500">Total Automations</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowAutomationConfig;